import { AxiosError } from 'axios';
import { Outlet, useLocation } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';
import { useEffect } from 'react';

import { Iconlabel } from '@wk/components-react16';

import AppLayout from '@/shared/layout/AppLayout';
import Header from '@/shared/layout/Header';
import Footer from '@/shared/layout/Footer';
import { getUserRoleAccess } from '@/domains/access/accessApi';
import { isNewsletterEnabledBySwagger } from '@/domains/newsletter/newsletterApi';
import useApplicationsStore from '@/domains/application/applicationStore';
import type { ErrorResponse } from '@/types';

const shouldUseMobileDocumentFlow = (pathname: string) =>
  pathname.startsWith('/applications') ||
  pathname.startsWith('/evaluations') ||
  pathname.startsWith('/settings') ||
  pathname.startsWith('/newsletter');

const shouldAwaitRouteLoadingSignal = (pathname: string) =>
  pathname === '/applications' ||
  (pathname.startsWith('/applications/') &&
    !pathname.startsWith('/applications/onboard')) ||
  pathname.startsWith('/evaluations');

const openEvaluationsMobileFilters = () => {
  window.dispatchEvent(new CustomEvent('evaluations:open-mobile-filters'));
};

const OrrLayout = () => {
  const location = useLocation();

  const { isLoading, error: applicationsError, fetchApplications } = useApplicationsStore(
    useShallow((state) => ({
      isLoading: state.isLoading,
      error: state.error,
      fetchApplications: state.fetchApplications,
    })),
  );

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const { isLoading: isUserRoleAccessLoading } = useQuery({
    queryKey: ['userRoleAccess'],
    queryFn: getUserRoleAccess,
  });
  const {
    isLoading: isNewsletterAvailabilityLoading,
    data: isNewsletterAvailable = false,
  } = useQuery({
    queryKey: ['newsletter', 'availability'],
    queryFn: isNewsletterEnabledBySwagger,
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const isInitialLayoutLoading =
    isLoading || isUserRoleAccessLoading || isNewsletterAvailabilityLoading;

  const renderContent = () => {
    if (applicationsError) {
      const appError = applicationsError as AxiosError<ErrorResponse>;
      return (
        <div className="w-full h-full justify-center items-center flex flex-col gap-2">
          <h2 className="text-lg font-bold">Error</h2>
          <p className="text-sm text-gray-500">
            {appError?.response?.data.title ||
              'An error occurred while fetching application data.'}
          </p>
        </div>
      );
    }

    if (isInitialLayoutLoading) {
      return (
        <div className="flex w-full flex-1 min-h-[55vh] items-center justify-center">
          <span className="animate-spin">
            <Iconlabel name="spinner-alt" />
          </span>
        </div>
      );
    }
    return <Outlet context={{ isNewsletterAvailable }} />;
  };

  return (
    <AppLayout
      header={
        <Header
          showMobileMenuButton={location.pathname === '/applications'}
          onMobileMenuClick={openEvaluationsMobileFilters}
        />
      }
      footer={<Footer isNewsletterAvailable={isNewsletterAvailable} />}
      shouldUseMobileDocumentFlow={shouldUseMobileDocumentFlow}
      shouldAwaitRouteLoadingSignal={shouldAwaitRouteLoadingSignal}
      isContentLoading={isInitialLayoutLoading}
    >
      {renderContent()}
    </AppLayout>
  );
};

export default OrrLayout;
